import pick from "lodash/pick";
import { selectedPolygonTypes } from "../Common/Constants";
import { havePermission } from "../../permissions";
import LAYERS_PERMISSIONS from "../../permissions/Layers.json";
import { get, has, forEach } from "lodash";

const { social_media_behaviors } = selectedPolygonTypes;

// const getPopulationGridRange = ({ data }) => {
// 	let min = Number.MAX_SAFE_INTEGER;
// 	let max = Number.MIN_SAFE_INTEGER;
// 	for (let [, obj] of data.entries()) {
// 		if (obj.population < min) min = obj.population;
// 		if (obj.population > max) max = obj.population;
// 	}
// 	return { min, max };
// };

/**
 * Computes the minimum and maximum values for each sub-key under keys that start with "shop_data."
 *
 * @param {Array<Object>} dataArray - An array of objects containing shop_data keys.
 * @returns {Object} An object with the structure:
 * {
 *   "shop_data.age_groups": { "18 to 24": { min: ..., max: ... }, ... },
 *   "shop_data.gender": { "Female": { min: ..., max: ... }, "Male": { min: ..., max: ... } },
 *   ...
 * }
 */
function getAllMinMax(dataArray) {
	const result = {};

	dataArray.forEach(item => {
		// Iterate over each key in the object.
		Object.keys(item).forEach(key => {
			if (key.startsWith("shop_data.")) {
				// Expecting the value to be an array of objects.
				const arr = item[key];
				if (!Array.isArray(arr)) return;
				arr.forEach(subObj => {
					Object.keys(subObj).forEach(subKey => {
						const value = subObj[subKey];
						if (typeof value !== "number") return;

						// Initialize the structure if it doesn't exist.

						if (!result[subKey]) {
							result[subKey] = { min: value, max: value };
						} else {
							result[subKey].min = Math.min(result[subKey].min, value);
							result[subKey].max = Math.max(result[subKey].max, value);
						}
					});
				});
			}
		});
	});

	return result;
}

const getSocialMediaBehaviorsPolygon = locations => {
	return {
		polygons: locations.map((obj, ind) => {
			const { flag, popular_times, population, sec, motor_sports_population } = obj;
			return {
				id: ind,
				_id: ind,
				...obj,
				coordinates: obj.coordinates,
				style: {
					fillColor: "#FF000077",
					strokeColor: "#000000",
					zindex: 10
				},
				filterData: {
					flag,
					popular_times,
					population,
					sec,
					motor_sports_population
				},

				polygonClick: true,
				type: social_media_behaviors
			};
		})
	};
};

const getSocialMediaBehaviorsSlider = ({ sliderHandler, type, locations }) => {
	if (!havePermission(LAYERS_PERMISSIONS.LAYER_SOCIAL_MEDIA_BEHAVIOURS_SLIDER)) return null;
	// let { min, max } = getPopulationGridRange({ data: locations });
	const dynamicMinMax = getAllMinMax(locations);
	return {
		min: 0,
		max: 100,
		sliderHandler,
		type,
		dynamicMinMax
	};
};

const SocialMediaBehaviorsService = {
	all: ({ locations, sliderHandler, type }) => {
		return {
			...pick(getSocialMediaBehaviorsPolygon(locations), ["polygons"]),
			...{ slider: getSocialMediaBehaviorsSlider({ sliderHandler, locations, type }) }
		};
	},
	polygons: ({ locations }) => {
		return pick(getSocialMediaBehaviorsPolygon(locations), ["polygons"]);
	}
};

export default SocialMediaBehaviorsService;
export { getSocialMediaBehaviorsPolygon, getSocialMediaBehaviorsSlider };

/**
 * Returns a single merged array of values from shopper_profile
 * for the specified shop_data keys.
 *
 * @param {Object} data - The main object.
 * @returns {Array} Merged array of values from shopper_profile, or [] if missing.
 */
export const getSocialMediaBehaviourValues = data => {
	if (!data?.digital_persona) return [];

	const profile = data.digital_persona;
	// Specify which keys you want to merge.
	const keysToMerge = ["shop_data.behaviors", "shop_data.interests", "shop_data.relationship_status"];

	// Merge arrays from the specified keys.
	const test = keysToMerge.reduce((acc, key) => {
		if (Array.isArray(profile[key])) {
			return acc.concat(profile[key]);
		}
		return acc;
	}, []);

	return test;
};

/**
 * Returns the average min and max values for the given demographic filter keys.
 *
 * @param {Array<string>} demographicFilterKeys - Array of keys to look up.
 * @param {Object} dynamicMinMax - Object with keys mapping to {min, max} objects.
 * @returns {{min: number|null, max: number|null}} - Object containing the average min and max values.
 */
export const getAverageMinMax = (demographicFilterKeys, dynamicMinMax, selectedPolygonType, defaultMinMax) => {
	if (selectedPolygonType !== social_media_behaviors) {
		return defaultMinMax;
	}
	let sumMin = 0;
	let sumMax = 0;
	let count = 0;

	forEach(demographicFilterKeys, key => {
		if (has(dynamicMinMax, key)) {
			sumMin += get(dynamicMinMax, `${key}.min`, 0);
			sumMax += get(dynamicMinMax, `${key}.max`, 0);
			count++;
		}
	});

	if (count === 0) {
		return { min: null, max: null };
	}

	return {
		min: Math.floor(sumMin / count),
		max: Math.floor(sumMax / count)
	};
};
